import { useId } from 'react';

type OverdueTasksClockWatermarkProps = {
  className?: string;
  /** When true, show red overdue alert badge on the clock. */
  showAlert?: boolean;
};

/** Decorative overdue clock watermark for employer dashboard overdue card. */
export const OverdueTasksClockWatermark = ({
  className = '',
  showAlert = true,
}: OverdueTasksClockWatermarkProps) => {
  const uid = useId().replace(/:/g, '');
  const lightClockId = `overdueClock-light-${uid}`;
  const darkClockId = `overdueClock-dark-${uid}`;

  const renderClock = (
    clockId: string,
    colors: {
      bodyStroke: string;
      bodyFill: string;
      faceStroke: string;
      faceFill: string;
      marker: string;
      hand: string;
      center: string;
      alertFill: string;
      alertStroke: string;
      alertMark: string;
    },
  ) => (
    <g id={clockId}>
      <circle cx="100" cy="100" r="80" stroke={colors.bodyStroke} strokeWidth="16" fill={colors.bodyFill} />
      <circle cx="100" cy="100" r="65" stroke={colors.faceStroke} strokeWidth="8" fill={colors.faceFill} />
      <line x1="100" y1="35" x2="100" y2="48" stroke={colors.marker} strokeWidth="6" strokeLinecap="round" />
      <line x1="165" y1="100" x2="152" y2="100" stroke={colors.marker} strokeWidth="6" strokeLinecap="round" />
      <line x1="100" y1="165" x2="100" y2="152" stroke={colors.marker} strokeWidth="6" strokeLinecap="round" />
      <line x1="35" y1="100" x2="48" y2="100" stroke={colors.marker} strokeWidth="6" strokeLinecap="round" />
      <line x1="100" y1="45" x2="100" y2="52" stroke={colors.marker} strokeWidth="4" strokeLinecap="round" />
      <line x1="135" y1="100" x2="142" y2="100" stroke={colors.marker} strokeWidth="4" strokeLinecap="round" />
      <line x1="100" y1="155" x2="100" y2="148" stroke={colors.marker} strokeWidth="4" strokeLinecap="round" />
      <line x1="65" y1="100" x2="58" y2="100" stroke={colors.marker} strokeWidth="4" strokeLinecap="round" />
      <line x1="100" y1="100" x2="78" y2="65" stroke={colors.hand} strokeWidth="8" strokeLinecap="round" />
      <line x1="100" y1="100" x2="100" y2="55" stroke={colors.hand} strokeWidth="5" strokeLinecap="round" />
      <circle cx="100" cy="100" r="7" fill={colors.center} />
      {showAlert ? (
        <>
          <circle
            cx="155"
            cy="145"
            r="35"
            fill={colors.alertFill}
            stroke={colors.alertStroke}
            strokeWidth="6"
          />
          <path
            d="M155 125 L155 155"
            stroke={colors.alertMark}
            strokeWidth="9"
            strokeLinecap="round"
          />
          <circle cx="155" cy="170" r="6" fill={colors.alertMark} />
        </>
      ) : null}
    </g>
  );

  return (
    <svg
      className={className}
      width="200"
      height="200"
      viewBox="0 0 200 200"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      <defs>
        {renderClock(lightClockId, {
          bodyStroke: '#64748B',
          bodyFill: '#E2E8F0',
          faceStroke: '#94A3B8',
          faceFill: '#F1F5F9',
          marker: '#CBD5E1',
          hand: '#94A3B8',
          center: '#CBD5E1',
          alertFill: '#FB7185',
          alertStroke: '#FFFFFF',
          alertMark: '#FFFFFF',
        })}
        {renderClock(darkClockId, {
          bodyStroke: '#475569',
          bodyFill: '#1E293B',
          faceStroke: '#64748B',
          faceFill: '#334155',
          marker: '#E2E8F0',
          hand: '#E2E8F0',
          center: '#E2E8F0',
          alertFill: '#F87171',
          alertStroke: '#FFFFFF',
          alertMark: '#FFFFFF',
        })}
      </defs>

      <g className="dark:hidden">
        <use href={`#${lightClockId}`} opacity="0.28" />
      </g>
      <g className="hidden dark:block">
        <use href={`#${darkClockId}`} opacity="0.32" />
      </g>
    </svg>
  );
};
